import React, { useState } from 'react';
import {
  LockClosedIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { apiService } from '../../services/apiService';

const TASK_STATUS_LABELS = { 1: 'To Do', 2: 'In Progress', 3: 'Done', 4: 'Blocked' };
const TASK_STATUS_COLORS = {
  1: 'bg-gray-200 text-gray-600',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-green-100 text-green-700',
  4: 'bg-red-100 text-red-700',
};

function PhaseTimeline({ phases, currentPhaseId }) {
  if (phases.length === 0) return null;
  return (
    <div className="flex items-center gap-2 mb-6 overflow-x-auto py-2">
      {phases.map((phase, i) => {
        const isComplete = phase.status === 3;
        const isActive = phase.status === 2;
        const isLast = i === phases.length - 1;
        return (
          <React.Fragment key={phase.id}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors
                ${isComplete ? 'bg-green-500 border-green-500 text-white' :
                  isActive ? 'bg-blue-600 border-blue-600 text-white' :
                  'bg-gray-100 border-gray-300 text-gray-400'}`}>
                {isComplete ? <CheckIcon className="h-4 w-4" /> : phase.sequenceOrder}
              </div>
              <span className={`text-xs mt-1 max-w-[80px] text-center leading-tight
                ${isComplete ? 'text-green-600' : isActive ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                {phase.name}
              </span>
            </div>
            {!isLast && (
              <div className={`flex-1 h-0.5 min-w-[20px] ${isComplete ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function PhaseCard({ phase, tasks, projectId, onSignOff, onTaskStatusChange, onAddTask }) {
  const [expanded, setExpanded] = useState(phase.status === 2); // Active expanded by default
  const [signingOff, setSigningOff] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [addingTask, setAddingTask] = useState(false);

  const isComplete = phase.status === 3;
  const isActive = phase.status === 2;
  const isLocked = phase.status === 1;

  const phaseTasks = tasks.filter(t => t.phaseId === phase.id);
  const allDone = phaseTasks.length > 0 && phaseTasks.every(t => t.status === 3);

  const borderColor = isComplete ? 'border-l-green-500' : isActive ? 'border-l-blue-500' : 'border-l-gray-300';

  const handleSignOff = async () => {
    if (!allDone) return;
    if (!window.confirm(`Sign off phase "${phase.name}"? This is irreversible.`)) return;
    setSigningOff(true);
    try {
      await onSignOff(phase.id);
    } finally {
      setSigningOff(false);
    }
  };

  const handleToggleTask = async (task) => {
    if (isLocked) return;
    const newStatus = task.status === 3 ? 1 : 3; // toggle Done / To Do
    await onTaskStatusChange(task.id, newStatus);
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    setAddingTask(true);
    try {
      await onAddTask({ title: newTaskTitle.trim(), phaseId: phase.id, status: 1 });
      setNewTaskTitle('');
      setShowAddTask(false);
    } finally {
      setAddingTask(false);
    }
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 border-l-4 ${borderColor} mb-3 ${isLocked ? 'opacity-60' : ''}`}>
      {/* Phase header */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {isComplete ? (
            <CheckCircleSolid className="h-5 w-5 text-green-500 flex-shrink-0" />
          ) : isLocked ? (
            <LockClosedIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ClockIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
          )}
          <div>
            <span className="font-medium text-gray-900 text-sm">{phase.name}</span>
            {(phase.startDate || phase.endDate) && (
              <span className="text-xs text-gray-400 ml-2">
                {phase.startDate ? new Date(phase.startDate).toLocaleDateString() : '?'}
                {' – '}
                {phase.endDate ? new Date(phase.endDate).toLocaleDateString() : '?'}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{phase.doneTaskCount}/{phase.taskCount}</span>
            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
                style={{ width: `${phase.progressPercent}%` }}
              />
            </div>
          </div>
          {expanded ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {/* Phase content */}
      {expanded && (
        <div className="px-4 pb-4">
          {isLocked && (
            <div className="flex items-center gap-2 py-2 mb-3 bg-gray-50 rounded-md px-3 text-xs text-gray-500">
              <LockClosedIcon className="h-3.5 w-3.5" />
              This phase is locked. Complete the previous phase to unlock it.
            </div>
          )}

          {/* Task checklist */}
          <div className="space-y-2">
            {phaseTasks.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-1">No tasks in this phase.</p>
            ) : (
              phaseTasks.map(task => (
                <div key={task.id} className={`flex items-start gap-3 py-1.5 ${isLocked ? 'pointer-events-none' : ''}`}>
                  <button
                    onClick={() => handleToggleTask(task)}
                    className={`flex-shrink-0 h-4 w-4 mt-0.5 rounded border-2 flex items-center justify-center transition-colors
                      ${task.status === 3 ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-blue-400'}`}
                  >
                    {task.status === 3 && <CheckIcon className="h-3 w-3 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${task.status === 3 ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                      {task.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {task.assignedToName && (
                      <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                        {task.assignedToName[0].toUpperCase()}
                      </div>
                    )}
                    {task.dueDate && (
                      <span className="text-xs text-gray-400">{new Date(task.dueDate).toLocaleDateString()}</span>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${TASK_STATUS_COLORS[task.status]}`}>
                      {TASK_STATUS_LABELS[task.status]}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add task row */}
          {isActive && (
            <div className="mt-3">
              {showAddTask ? (
                <form onSubmit={handleAddTask} className="flex gap-2">
                  <input
                    autoFocus
                    value={newTaskTitle}
                    onChange={e => setNewTaskTitle(e.target.value)}
                    placeholder="Task title…"
                    className="flex-1 px-2 py-1.5 text-xs border border-blue-400 rounded-md focus:outline-none"
                  />
                  <button type="submit" disabled={addingTask || !newTaskTitle.trim()}
                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md disabled:opacity-50">
                    Add
                  </button>
                  <button type="button" onClick={() => setShowAddTask(false)}
                    className="px-2 py-1 text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </form>
              ) : (
                <button onClick={() => setShowAddTask(true)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors">
                  <PlusIcon className="h-3.5 w-3.5" />
                  Add task to phase
                </button>
              )}
            </div>
          )}

          {/* Sign-off button */}
          {isActive && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <button
                onClick={handleSignOff}
                disabled={!allDone || signingOff}
                className={`w-full py-2 text-sm font-medium rounded-md transition-colors
                  ${allDone
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
              >
                {signingOff ? 'Signing off…' :
                  allDone ? '✓ Sign off phase' : `Complete all tasks to sign off (${phaseTasks.filter(t => t.status !== 3).length} remaining)`}
              </button>
            </div>
          )}

          {isComplete && phase.signedOffAt && (
            <p className="mt-3 text-xs text-green-600">
              ✓ Signed off by {phase.signedOffByName || 'Unknown'} on {new Date(phase.signedOffAt).toLocaleDateString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function WaterfallView({ projectId, tasks, setTasks, phases, setPhases, onAddTask }) {
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [phaseForm, setPhaseForm] = useState({ name: '', startDate: '', endDate: '' });
  const [savingPhase, setSavingPhase] = useState(false);

  const unassignedTasks = tasks.filter(t => !t.phaseId);

  const handleSignOff = async (phaseId) => {
    try {
      const updated = await apiService.signOffPhase(projectId, phaseId);
      setPhases(prev => prev.map(p =>
        p.id === phaseId ? updated :
        p.sequenceOrder === updated.sequenceOrder + 1 ? { ...p, status: 2 } : p
      ));
      // Refresh phases
      const fresh = await apiService.getProjectPhases(projectId);
      setPhases(fresh);
    } catch (err) {
      alert(err.message || 'Failed to sign off phase');
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    try {
      await apiService.updateProjectTask(projectId, taskId, { status: newStatus });
    } catch (err) {
      // Reload tasks on error
      const freshTasks = await apiService.getProjectTasks(projectId);
      setTasks(freshTasks);
    }
    // Recompute phase progress
    const fresh = await apiService.getProjectPhases(projectId);
    setPhases(fresh);
  };

  const handleAddTaskToPhase = async (taskData) => {
    const task = await apiService.createProjectTask(projectId, taskData);
    setTasks(prev => [...prev, task]);
    const fresh = await apiService.getProjectPhases(projectId);
    setPhases(fresh);
  };

  const handleAddPhase = async (e) => {
    e.preventDefault();
    if (!phaseForm.name.trim()) return;
    setSavingPhase(true);
    try {
      const phase = await apiService.createProjectPhase(projectId, {
        name: phaseForm.name.trim(),
        startDate: phaseForm.startDate || null,
        endDate: phaseForm.endDate || null,
      });
      setPhases(prev => [...prev, phase]);
      setPhaseForm({ name: '', startDate: '', endDate: '' });
      setShowAddPhase(false);
    } catch (err) {
      alert('Failed to add phase');
    } finally {
      setSavingPhase(false);
    }
  };

  if (phases.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <p className="text-gray-500 font-medium mb-1">No phases defined yet</p>
        <p className="text-gray-400 text-sm mb-4">Add phases to use the waterfall view.</p>
        <button onClick={() => setShowAddPhase(true)}
          className="flex items-center gap-2 mx-auto px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
          <PlusIcon className="h-4 w-4" />
          Add First Phase
        </button>
        {showAddPhase && (
          <form onSubmit={handleAddPhase} className="mt-4 max-w-sm mx-auto text-left space-y-3">
            <input required value={phaseForm.name} onChange={e => setPhaseForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Phase name" autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <div className="flex gap-2">
              <input type="date" value={phaseForm.startDate} onChange={e => setPhaseForm(p => ({ ...p, startDate: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm" />
              <input type="date" value={phaseForm.endDate} onChange={e => setPhaseForm(p => ({ ...p, endDate: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={savingPhase}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md disabled:opacity-50">
                {savingPhase ? 'Adding…' : 'Add Phase'}
              </button>
              <button type="button" onClick={() => setShowAddPhase(false)}
                className="px-4 py-2 text-gray-600 text-sm rounded-md hover:bg-gray-100">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div>
      <PhaseTimeline phases={phases} />

      {/* Unassigned tasks bucket */}
      {unassignedTasks.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">Unassigned Tasks ({unassignedTasks.length})</h4>
          <div className="space-y-1.5">
            {unassignedTasks.map(task => (
              <div key={task.id} className="flex items-center gap-2 text-sm text-yellow-700">
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${task.status === 3 ? 'bg-green-400' : 'bg-yellow-400'}`} />
                {task.title}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase cards */}
      {phases.map(phase => (
        <PhaseCard
          key={phase.id}
          phase={phase}
          tasks={tasks}
          projectId={projectId}
          onSignOff={handleSignOff}
          onTaskStatusChange={handleTaskStatusChange}
          onAddTask={handleAddTaskToPhase}
        />
      ))}

      {/* Add Phase button */}
      <div className="mt-4">
        {showAddPhase ? (
          <form onSubmit={handleAddPhase} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Add Phase</h4>
            <input required value={phaseForm.name} onChange={e => setPhaseForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Phase name" autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={phaseForm.startDate} onChange={e => setPhaseForm(p => ({ ...p, startDate: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Start date" />
              <input type="date" value={phaseForm.endDate} onChange={e => setPhaseForm(p => ({ ...p, endDate: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="End date" />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={savingPhase}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50">
                {savingPhase ? 'Adding…' : 'Add Phase'}
              </button>
              <button type="button" onClick={() => setShowAddPhase(false)}
                className="px-4 py-2 text-gray-600 text-sm rounded-md hover:bg-gray-100">
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button onClick={() => setShowAddPhase(true)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-blue-600 transition-colors">
            <PlusIcon className="h-4 w-4" />
            Add Phase
          </button>
        )}
      </div>
    </div>
  );
}
